<template>
  <div class="services-page" v-loading="loading">

    <!-- Stats Row -->
    <div class="row mb-4 stats-row mt-5">
      <div class="col-md-4">
        <div class="stat-card">
          <div class="stat-label">{{ $t('ongoing_requests') }}</div>
          <div class="stat-value text-primary">{{ ongoingOrdersCount }}</div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="stat-card stat-card--green">
          <div class="stat-label">{{ $t('active_services') }}</div>
          <div class="stat-value text-success">{{ activeServicesCount }}</div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="stat-card">
          <div class="stat-label text-muted">{{ $t('total_services') }}</div>
          <div class="stat-value">{{ services.length }}</div>
        </div>
      </div>
    </div>

    <!-- Search + Add -->
    <div class="d-flex align-items-center mb-3" style="gap: 12px;">
      <div class="search-wrap flex-grow-1">
        <i class="el-icon-search search-icon"></i>
        <input
          v-model="search"
          class="search-input"
          :placeholder="$t('search_by_name_or_slug')"
        />
      </div>
      <button class="btn-add" @click="openCreateModal">
        + {{ $t('add_service') }}
      </button>
    </div>

    <!-- Price Filter -->
    <!-- <div class="price-filter-row mb-3">
      <div class="price-filter-label">
        <i class="el-icon-price-tag"></i> {{ $t('filter_by_price') }}
      </div>
      <div class="price-range-inputs">
        <div class="price-input-wrap">
          <span class="price-currency">{{ $t('currency_sar') }}</span>
          <input
            v-model.number="priceMin"
            type="number"
            min="0"
            class="price-input"
            :placeholder="$t('min_price')"
          />
        </div>
        <span class="price-separator">—</span>
        <div class="price-input-wrap">
          <span class="price-currency">{{ $t('currency_sar') }}</span>
          <input
            v-model.number="priceMax"
            type="number"
            min="0"
            class="price-input"
            :placeholder="$t('max_price')"
          />
        </div>
        <button v-if="priceMin !== '' || priceMax !== ''" class="price-clear-btn" @click="clearPriceFilter">
          {{ $t('clear') }}
        </button>
      </div>
    </div> -->

    <!-- Table -->
    <div class="services-table-wrap">
      <table class="services-table">
        <thead>
          <tr>
            <th class="col-service">{{ $t('service') }}</th>
            <th class="col-category">{{ $t('category') }}</th>
            <th class="col-price col-price--sortable" @click="sortByPrice">
              {{ $t('price') }}
              <span class="sort-icon">
                <i v-if="priceSort === null" class="el-icon-sort"></i>
                <i v-else-if="priceSort === 'asc'" class="el-icon-sort-up sort-active"></i>
                <i v-else class="el-icon-sort-down sort-active"></i>
              </span>
            </th>
            <th class="col-orders">{{ $t('ongoing_orders') }}</th>
            <th class="col-status">{{ $t('status') }}</th>
            <th class="col-toggle">{{ $t('enable') }}</th>
            <th class="col-toggle"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(service, index) in filteredServices"
            :key="service.id"
            class="service-row"
            :class="{ 'drag-over': dragOverIndex === index }"
            draggable="true"
            @dragstart="onDragStart($event, index)"
            @dragover.prevent="onDragOver($event, index)"
            @dragleave="dragOverIndex = null"
            @drop="onDrop($event, index)"
          >
            <!-- Service name + icon -->
            <td class="col-service">
              <div class="service-cell">
                <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                <div class="service-icon">
                  <img v-if="service.icon_url" :src="service.icon_url" width="32" height="32" class="rounded" />
                  <div v-else class="icon-placeholder">
                    <i class="el-icon-box"></i>
                  </div>
                </div>
                <div class="service-info">
                  <span class="service-name">{{ service.name_ar }}</span>
                  <span v-if="service.name_en" class="service-name-en">{{ service.name_en }}</span>
                  <span class="service-slug">{{ service.slug }}</span>
                </div>
              </div>
            </td>

            <!-- Category badge -->
            <td class="col-category">
              <span
                v-if="service.available_for_individuals && service.available_for_merchants"
                class="badge badge--purple"
              >
                <i class="el-icon-user-solid"></i> {{ $t('everyone') }}
              </span>
              <span
                v-else-if="service.available_for_individuals"
                class="badge badge--blue"
              >
                <i class="el-icon-user"></i> {{ $t('individuals') }}
              </span>
              <span
                v-else-if="service.available_for_merchants"
                class="badge badge--orange"
              >
                <i class="el-icon-s-shop"></i> {{ $t('stores') }}
              </span>
            </td>

            <!-- Price -->
            <td class="col-price">
              <span class="price-text">{{ service.fixed_price }} {{ $t('currency_sar') }}</span>
            </td>

            <!-- Ongoing orders -->
            <td class="col-orders">
              <span v-if="service.active_orders_count " class="orders-badge">
                {{ service.active_orders_count }}
              </span>
              <span v-else class="text-muted mx-2">—</span>
            </td>

            <!-- Status -->
            <td class="col-status">
              <span class="status-dot" :class="service.is_active ? 'status-dot--active' : 'status-dot--inactive'"></span>
              <span :class="service.is_active ? 'text-success' : 'text-muted'">
                {{ service.is_active ? $t('active') : $t('inactive') }}
              </span>
            </td>

            <td>
                <el-switch v-model="service.is_active" @change="toggleStatus(service)"
                    active-color="#00C48C" inactive-color="#DCDFE6"></el-switch>
            </td>

            <!-- Toggle + Actions -->
            <td class="col-toggle">
              <div class="actions-cell">
                <button class="action-btn action-btn--edit" @click.stop="editService(service)">
                  <i class="el-icon-edit"></i>
                </button>
                <button class="action-btn action-btn--delete" @click.stop="confirmDelete(service)">
                  <i class="el-icon-delete"></i>
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="filteredServices.length === 0 && !loading">
            <td colspan="6" class="empty-state">
              <i class="el-icon-inbox"></i>
              <p>{{ $t('no_data_found') }}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Footer hint -->
      <div class="table-footer-hint">
        <i class="el-icon-rank"></i>
        {{ $t('drag_rows_to_reorder') }} — {{ $t('save_order') }}
      </div>
    </div>

    <!-- Form Modal -->
    <FormModal ref="formModal" @saved="fetchServices" />
  </div>
</template>

<script>
import { getServices, updateService, deleteService, updateServicesOrder } from '@/api/services';
import FormModal from './FormModal.vue';
import mixin from "@/mixins/common";
import Swal from 'sweetalert2';

export default {
  name: 'ServicesIndex',
  components: { FormModal },
  mixins: [mixin],
  data() {
    return {
      services: [],
      totalOngoingOrders: 0,
      search: '',
      loading: false,
      draggedItemIndex: null,
      priceMin: '',
      priceMax: '',
      priceSort: null,   // null | 'asc' | 'desc'
      dragOverIndex: null,
    };
  },
  computed: {
    activeServicesCount() {
      return this.services.filter(s => s.is_active).length;
    },
    ongoingOrdersCount() {
      return this.totalOngoingOrders;
    },
    filteredServices() {
      let list = this.services;

      // Text search
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(s =>
          (s.name_ar && s.name_ar.toLowerCase().includes(q)) ||
          (s.name_en && s.name_en.toLowerCase().includes(q)) ||
          (s.slug && s.slug.toLowerCase().includes(q))
        );
      }

      // Price range
      if (this.priceMin !== '' && this.priceMin !== null) {
        list = list.filter(s => parseFloat(s.fixed_price) >= parseFloat(this.priceMin));
      }
      if (this.priceMax !== '' && this.priceMax !== null) {
        list = list.filter(s => parseFloat(s.fixed_price) <= parseFloat(this.priceMax));
      }

      // Price sort
      if (this.priceSort) {
        list = [...list].sort((a, b) => {
          const diff = parseFloat(a.fixed_price) - parseFloat(b.fixed_price);
          return this.priceSort === 'asc' ? diff : -diff;
        });
      }

      return list;
    },
  },
  mounted() {
    this.fetchServices();
  },
  methods: {
    async fetchServices() {
      this.loading = true;
      try {
        const response = await getServices();
        this.services = response.data || [];
        this.totalOngoingOrders = response.meta?.total_ongoing_orders ?? 0;
      } catch (error) {
        console.error(error);
        this.runToast(this.$t('error_fetching_data'), 'error');
      } finally {
        this.loading = false;
      }
    },
    clearPriceFilter() {
      this.priceMin = '';
      this.priceMax = '';
    },
    sortByPrice() {
      if (this.priceSort === null)       this.priceSort = 'asc';
      else if (this.priceSort === 'asc') this.priceSort = 'desc';
      else                               this.priceSort = null;
    },
    openCreateModal() {
      this.$refs.formModal.open();
    },
    editService(service) {
      this.$refs.formModal.open(service);
    },
    async toggleStatus(service) {
      try {
        const formData = new FormData();
        formData.append('name_ar', service.name_ar);
        formData.append('name_en', service.name_en);
        formData.append('fixed_price', service.fixed_price);
        formData.append('is_active', service.is_active ? 1 : 0);
        await updateService(service.id, formData);
        this.runToast(this.$t('status_updated'), 'success');
      } catch (error) {
        service.is_active = !service.is_active; // revert
        this.runToast(this.$t('error_updating_status'), 'error');
      }
    },
    confirmDelete(service) {
      Swal.fire({
        title: this.$t('are_you_sure'),
        text: this.$t('delete_service_warning'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete'),
        cancelButtonText: this.$t('cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteService(service.id);
            this.runToast(this.$t('deleted_successfully'), 'success');
            this.fetchServices();
          } catch (error) {
            if (error.response && error.response.status === 422) {
              Swal.fire({
                title: this.$t('cannot_delete_service'),
                text: error.response.data.message || this.$t('service_linked_warning'),
                icon: 'warning',
                confirmButtonText: this.$t('ok'),
              });
            } else {
              this.runToast(this.$t('error_deleting'), 'error');
            }
          }
        }
      });
    },
    onDragStart(event, index) {
      this.draggedItemIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    onDragOver(event, index) {
      this.dragOverIndex = index;
    },
    async onDrop(event, index) {
      this.dragOverIndex = null;
      if (this.draggedItemIndex === null || this.draggedItemIndex === index) return;

      if (this.search) {
        this.runToast(this.$t('cannot_reorder_while_searching'), 'warning');
        return;
      }

      if (this.priceSort) {
        this.runToast(this.$t('cannot_reorder_while_sorted'), 'warning');
        return;
      }

      const dragged = this.services.splice(this.draggedItemIndex, 1)[0];
      this.services.splice(index, 0, dragged);

      const updatedServices = this.services.map((s, i) => {
        s.sort_order = i + 1;
        return { id: s.id, sort_order: s.sort_order };
      });

      this.draggedItemIndex = null;

      try {
        await updateServicesOrder({ services: updatedServices });
        this.runToast(this.$t('order_saved'), 'success');
      } catch (error) {
        this.runToast(this.$t('error_saving_order'), 'error');
        this.fetchServices();
      }
    },
  },
};
</script>

<style scoped>
/* ─── Page ─────────────────────────────────────── */
.services-page {
  padding: 0 4px;
  direction: rtl;
}

/* ─── Stats Cards ───────────────────────────────── */
.stats-row {
  direction: rtl;
}
.stat-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 20px 24px;
  text-align: right;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.stat-card--green {
  background: #f0fdf6;
  border-color: #d1fae5;
}
.stat-label {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

/* ─── Price Filter ─────────────────────────────── */
.price-filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 10px 16px;
  direction: rtl;
}
.price-filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
}
.price-range-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}
.price-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 10px;
  height: 36px;
}
.price-currency {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
}
.price-input {
  width: 90px;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #111827;
  text-align: right;
}
.price-input::placeholder { color: #d1d5db; }
.price-separator {
  color: #d1d5db;
  font-size: 16px;
}
.price-clear-btn {
  background: none;
  border: none;
  color: #6366f1;
  font-size: 13px;
  cursor: pointer;
  padding: 0 6px;
  white-space: nowrap;
}
.price-clear-btn:hover { text-decoration: underline; }

/* ─── Search ────────────────────────────────────── */
.search-wrap {
  position: relative;
}
.search-icon {
  position: absolute;
  left: auto;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 16px;
}
.search-input {
  width: 100%;
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 40px 0 14px;
  font-size: 14px;
  outline: none;
  direction: rtl;
  background: #fff;
  transition: border-color .2s;
}
.search-input:focus {
  border-color: #6366f1;
}

/* ─── Add Button ────────────────────────────────── */
.btn-add {
  flex-shrink: 0;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0 20px;
  height: 40px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background .2s;
}
.btn-add:hover {
  background: #4f46e5;
}

/* ─── Table ─────────────────────────────────────── */
.services-table-wrap {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.services-table {
  width: 100%;
  border-collapse: collapse;
  direction: rtl;
}
.services-table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #f0f0f0;
}
.services-table th {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-align: right;
  white-space: nowrap;
}
.services-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f9fafb;
  vertical-align: middle;
  text-align: right;
}
.service-row:last-child td {
  border-bottom: none;
}
.service-row:hover {
  background: #fafafa;
}
.service-row.drag-over {
  background: #f0f4ff;
}

/* ─── Service cell ──────────────────────────────── */
.service-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.drag-handle {
  color: #d1d5db;
  cursor: grab;
  font-size: 14px;
  letter-spacing: -2px;
  user-select: none;
}
.drag-handle:active { cursor: grabbing; }
.service-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}
.icon-placeholder {
  width: 36px;
  height: 36px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 16px;
}
.service-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.service-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.service-name-en {
  font-size: 12px;
  color: #6b7280;
  direction: ltr;
}
.service-slug {
  font-size: 11px;
  color: #9ca3af;
  direction: ltr;
}

/* ─── Category badges ───────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.badge--purple {
  background: #ede9fe;
  color: #7c3aed;
}
.badge--blue {
  background: #dbeafe;
  color: #2563eb;
}
.badge--orange {
  background: #fff7ed;
  color: #ea580c;
}

/* ─── Price ─────────────────────────────────────── */
/* ─── Sortable price column ─────────────────────── */
.col-price--sortable {
  cursor: pointer;
  user-select: none;
}
.col-price--sortable:hover {
  color: #4f46e5;
}
.sort-icon {
  margin-right: 4px;
  font-size: 12px;
  color: #9ca3af;
}
.sort-active {
  color: #6366f1 !important;
}

.price-text {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* ─── Orders badge ──────────────────────────────── */
.orders-badge {
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
}

/* ─── Status ────────────────────────────────────── */
.col-status {
  white-space: nowrap;
}
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
}
.status-dot--active  { background: #10b981; }
.status-dot--inactive { background: #d1d5db; }

/* ─── Actions cell ──────────────────────────────── */
.actions-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}
.action-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background .15s;
}
.action-btn--edit {

  color: #6b7280;
}
.action-btn--edit:hover {
  background: #e0e7ff;
  color: #4f46e5;
}
.action-btn--delete {

  color: #6b7280;
}
.action-btn--delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* ─── Empty state ───────────────────────────────── */
.empty-state {
  text-align: center;
  padding: 60px;
  color: #9ca3af;
}
.empty-state i {
  font-size: 40px;
  margin-bottom: 8px;
  display: block;
}

/* ─── Footer ────────────────────────────────────── */
.table-footer-hint {
  padding: 10px 16px;
  font-size: 12px;
  color: #9ca3af;
  border-top: 1px solid #f0f0f0;
  text-align: center;
}
</style>
